import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Container, GoldButton } from "@/components/shared";

const IMG = "https://images.pexels.com/photos/37125651/pexels-photo-37125651.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

export default function Story1630() {
  return (
    <div>
      <PageHero
        kicker="Cornerstone · The 1630 story"
        title="7 September 1630"
        intro="The day a settlement in Massachusetts Bay took the name of a market town in Lincolnshire — and the people who carried it there."
        dark
      />

      <article className="py-16 sm:py-24">
        <Container>
          <div className="max-w-3xl mx-auto">
            <img src={IMG} alt="St Botolph's Church, Boston, Lincolnshire" className="w-full h-[380px] object-cover border border-[#D6D3CB] mb-10" />

            <p className="font-playfair italic text-base text-[#CDA434] mb-6">The mother town, 1612</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1E1E2F] mb-4">John Cotton at St Botolph's</h2>
            <div className="space-y-5 font-sans text-base text-[#1B2430] leading-relaxed">
              <p>
                In 1612 John Cotton became vicar of St Botolph's, the great church in Boston,
                Lincolnshire, whose tower — the Stump — rises above the flat Fenland for miles
                around. Cotton was a learned and persuasive preacher, and under him the town became a
                gathering point for men and women of strong Puritan conviction who wanted a plainer,
                sterner church than the crown and its bishops would permit.
              </p>
              <p>
                For two decades Boston was a place where those ideas were argued, written down, and
                held to. But conformity was tightening, and for many the price of staying grew too
                high.
              </p>
            </div>

            <div className="my-12">
              <blockquote className="font-playfair italic text-xl sm:text-2xl text-[#1E1E2F] leading-relaxed border-l-2 border-[#CDA434] pl-6 py-2">
                “They carried little with them but their convictions — and, it turned out, a name.”
              </blockquote>
            </div>

            <p className="font-playfair italic text-base text-[#CDA434] mb-6">The crossing, 1630</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1E1E2F] mb-4">The Winthrop fleet</h2>
            <div className="space-y-5 font-sans text-base text-[#1B2430] leading-relaxed">
              <p>
                In 1630 a fleet of ships under John Winthrop carried around a thousand Puritan
                settlers to the Massachusetts Bay Colony. This was not the Plymouth of 1620 — those
                earlier settlers, often called Pilgrims, had landed a decade before and further
                south. The Massachusetts Bay migration was a larger, later movement, and it is from
                it that the city of Boston grew.
              </p>
              <p>
                Among the Bay Colony's leaders were men connected to Boston, Lincolnshire, and to
                Cotton's circle. When they established themselves on the peninsula, the old town was
                very much in mind.
              </p>
            </div>

            <p className="font-playfair italic text-base text-[#CDA434] mb-6 mt-12">The naming</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1E1E2F] mb-4">A new Boston</h2>
            <div className="space-y-5 font-sans text-base text-[#1B2430] leading-relaxed">
              <p>
                The settlement was first called Trimountaine, for the three hills of the peninsula.
                On 7 September 1630 it was renamed Boston, in honour of the Lincolnshire town so many
                of the settlers had left behind. John Cotton himself would cross to the new Boston a
                few years later, in 1633, and become one of its leading ministers.
              </p>
              <p>
                So the two towns came to share a single name: the older, the <strong>mother town</strong> on
                the edge of the Wash; the newer, the <strong>daughter city</strong> on Massachusetts Bay.
              </p>
            </div>

            <div className="my-12 bg-[#FFFAF3] border-l-4 border-[#CDA434] p-8">
              <p className="font-serif text-lg text-[#003366] mb-3">An honest word</p>
              <p className="font-sans text-base text-[#1B2430] leading-relaxed">
                The peninsula the settlers named was not empty. It was called Shawmut, and it was the
                land of the Massachusett people, who had lived, fished, and farmed there long before
                any ship arrived. The naming of a new Boston was, for them, the beginning of loss.
                A crossing worth keeping should say so plainly, and this one does.
              </p>
            </div>

            <div className="pt-6 border-t border-[#D6D3CB] flex flex-wrap gap-3">
              <GoldButton as="link" to="/2030">See the 2030 ledger <ArrowRight size={16} className="ml-2" /></GoldButton>
              <Link to="/journal" className="inline-flex items-center font-sans text-sm font-semibold text-[#003366] px-4 py-3 link-underline">
                More from the journal →
              </Link>
            </div>
          </div>
        </Container>
      </article>
    </div>
  );
}
